import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { getProactiveEngagement } from '../services/chat-api.service';
import type { Session, Store } from '../services/types';
import { useDebouncedCallback } from '@mantine/hooks';
import { useAnalytics } from './useAnalytics';
import { Featureflag } from '../constants/featureFlags';
import type { DisplayMessage } from '../context/use-chat-context';
import { useUserContext } from '../context/use-user-context';

const PROACTIVE_ENGAGEMENT_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const LAST_FETCH_KEY = 'proactive_engagement_last_fetch';
const INITIAL_DELAY = 5 * 1000; // 30 seconds delay

type ProactiveEngagementProps = {
  productId?: string;
  onGetProactiveEngagement?: (proactiveEngagement: DisplayMessage) => void;
  enabled?: boolean;
  store?: Store;
  session?: Session;
};

const shouldFetchProactiveEngagementForStore = (storeId: string): boolean => {
  const lastFetchData = localStorage.getItem(`${LAST_FETCH_KEY}_${storeId}`);
  if (!lastFetchData) return true;

  const lastFetchTime = parseInt(lastFetchData, 10);
  const currentTime = Date.now();

  return currentTime - lastFetchTime > PROACTIVE_ENGAGEMENT_TIMEOUT;
};

const updateLastFetchTimeForStore = (storeId: string): void => {
  localStorage.setItem(`${LAST_FETCH_KEY}_${storeId}`, Date.now().toString());
};

export const useGetProactiveEngagement = ({
  productId,
  onGetProactiveEngagement,
  enabled,
  store,
  session,
}: ProactiveEngagementProps) => {
  const [proactiveEngagement, setProactiveEngagement] =
    useState<DisplayMessage | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLoadingRef = useRef(isLoading);
  const { trackEvent } = useAnalytics();
  const { isFeatureEnabled } = useUserContext();

  const isFeatureFlagEnabled = isFeatureEnabled(Featureflag.ENGAGE);

  // Keep ref in sync with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const debounceDelay = useMemo(
    () =>
      store?.config?.config?.proactiveEngagementTimeoutSeconds
        ? store.config.config.proactiveEngagementTimeoutSeconds * 1000
        : INITIAL_DELAY,
    [store?.config?.config?.proactiveEngagementTimeoutSeconds]
  );

  // Create the base callback with useCallback to ensure it captures latest values
  const fetchProactiveEngagement = useCallback(
    async ({
      storeId,
      productIdentifier,
    }: {
      storeId: string;
      productIdentifier: string;
    }) => {
      if (isLoadingRef.current) return;
      if (!store) return;

      try {
        setIsLoading(true);
        trackEvent('engage_processing', {
          storeId,
          productIdentifier,
        });
        const response = await getProactiveEngagement({
          productIdentifier,
          session,
          store,
        });

        const message: DisplayMessage = {
          userQuery: '',
          systemAnswer: response.answer,
        };

        trackEvent('engage_message_displayed', {
          storeId,
          productIdentifier,
          engagementMessage: response.answer,
        });

        setProactiveEngagement(message);
        updateLastFetchTimeForStore(storeId);
        onGetProactiveEngagement?.(message);
      } catch (error) {
        console.error('Error getting proactive engagement:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [store, session, trackEvent, onGetProactiveEngagement]
  );

  // Debounce the callback - this will recreate when debounceDelay changes
  const initializeProactiveEngagement = useDebouncedCallback(
    fetchProactiveEngagement,
    debounceDelay
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect intentionally doesn't include all dependencies
  useEffect(() => {
    if (isLoading || !enabled || !store?.id || !isFeatureFlagEnabled) return;

    setProactiveEngagement(null);

    if (!productId || !shouldFetchProactiveEngagementForStore(store.id)) {
      return;
    }

    initializeProactiveEngagement({
      storeId: store.id,
      productIdentifier: productId,
    });
  }, [productId, enabled, store?.id, isFeatureFlagEnabled]);

  return { proactiveEngagement, isLoading };
};
